/**
 * "We dispatch your parcel today / tomorrow / on Monday".
 *
 * Worked out in the browser on every view, because the page is cached: a day decided at render
 * time would still claim "today" long after that day ended.
 *
 * Everything is judged in the SHOP's time zone, never the visitor's. The cutoff is the
 * warehouse's deadline, so a customer abroad must be told what the warehouse says. Intl handles
 * the zone, including daylight saving, without any offset arithmetic here.
 */
(function () {
  'use strict';

  const WEEKDAY_TO_ISO = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

  /**
   * The shop's own wall clock right now.
   * @param {string} timeZone IANA zone name.
   * @returns {{year:number, month:number, day:number, minutes:number, weekday:number}|null}
   */
  function shopNow(timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        weekday: 'short'
      }).formatToParts(new Date());

      const get = (type) => (parts.find((p) => p.type === type) || {}).value;

      /* hourCycle h23 can report midnight as "24" in some engines. */
      const hour = parseInt(get('hour'), 10) % 24;

      return {
        year: parseInt(get('year'), 10),
        month: parseInt(get('month'), 10),
        day: parseInt(get('day'), 10),
        minutes: hour * 60 + parseInt(get('minute'), 10),
        weekday: WEEKDAY_TO_ISO[get('weekday')]
      };
    } catch (error) {
      /* An unknown time zone name. Better to show nothing than a wrong dispatch day. */
      return null;
    }
  }

  /**
   * @param {string} value "14:00"
   * @returns {number|null} Minutes since midnight.
   */
  function parseCutoff(value) {
    const match = /^\s*(\d{1,2})[:.](\d{2})\s*$/.exec(value || '');
    if (!match) return null;

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (hours > 23 || minutes > 59) return null;

    return hours * 60 + minutes;
  }

  /**
   * How many days ahead the parcel goes out.
   * @param {Object} now From shopNow().
   * @param {number} cutoff Minutes since midnight.
   * @param {number[]} days Dispatch days as ISO weekday numbers.
   * @returns {number|null} 0 = today, 1 = tomorrow, and so on. Null if no day qualifies.
   */
  function daysUntilDispatch(now, cutoff, days) {
    if (days.indexOf(now.weekday) !== -1 && now.minutes < cutoff) return 0;

    /* Seven steps is enough to come back round to the same weekday; anything beyond that would
       repeat an answer already rejected. */
    for (let ahead = 1; ahead <= 7; ahead += 1) {
      const weekday = ((now.weekday - 1 + ahead) % 7) + 1;
      if (days.indexOf(weekday) !== -1) return ahead;
    }

    return null;
  }

  /**
   * @param {Object} now From shopNow().
   * @param {number} ahead Days from today.
   * @param {string} locale
   * @returns {string} The weekday name in the page's language.
   */
  function weekdayName(now, ahead, locale) {
    /* Built as a UTC date and formatted as UTC: this is plain calendar arithmetic, so keeping it
       out of any zone avoids a daylight-saving shift moving the name by a day. */
    const date = new Date(Date.UTC(now.year, now.month - 1, now.day + ahead));
    return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long' }).format(date);
  }

  /**
   * "monday, tuesday and friday" - or the shop's own phrase when it is the full working week.
   * @param {number[]} days ISO weekday numbers, ascending.
   * @param {string} locale
   * @param {Object} labels
   * @returns {string}
   */
  function daysPhrase(days, locale, labels) {
    const isWorkingWeek = days.length === 5 && days.every((d, i) => d === i + 1);
    if (isWorkingWeek && labels.allWeekdays) return labels.allWeekdays;

    /* 2024-01-01 was a Monday, so adding (iso - 1) lands on the right weekday. */
    const names = days.map((iso) =>
      new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'long' }).format(
        new Date(Date.UTC(2024, 0, iso))
      )
    );

    if (names.length === 1) return names[0];
    return names.slice(0, -1).join(', ') + ' ' + labels.and + ' ' + names[names.length - 1];
  }

  class ShippingCutoff extends HTMLElement {
    connectedCallback() {
      const cutoff = parseCutoff(this.dataset.cutoff);
      const days = (this.dataset.days || '')
        .split(',')
        .map((d) => parseInt(d, 10))
        .filter((d) => d >= 1 && d <= 7)
        .sort((a, b) => a - b);

      if (cutoff === null || !days.length) return;

      const now = shopNow(this.dataset.timezone || 'UTC');
      if (!now || !now.weekday) return;

      const ahead = daysUntilDispatch(now, cutoff, days);
      if (ahead === null) return;

      const locale = document.documentElement.lang || 'da';

      let text;
      if (ahead === 0) {
        text = this.dataset.today;
      } else if (ahead === 1) {
        text = this.dataset.tomorrow;
      } else {
        text = (this.dataset.onDay || '').replace('[day]', weekdayName(now, ahead, locale));
      }

      const dayEl = this.querySelector('[data-cutoff-day]');
      const noteEl = this.querySelector('[data-cutoff-note]');
      if (!dayEl || !text) return;

      dayEl.textContent = text;

      if (noteEl && this.dataset.cutoffText) {
        noteEl.textContent = this.dataset.cutoffText.replace(
          '[days]',
          daysPhrase(days, locale, { allWeekdays: this.dataset.allWeekdays, and: this.dataset.and || 'og' })
        );
      }

      this.hidden = false;
    }
  }

  /* Exposed so the logic can be exercised without a browser. */
  window.ZimpleShippingCutoff = { shopNow, parseCutoff, daysUntilDispatch, weekdayName, daysPhrase };

  if (!customElements.get('shipping-cutoff')) customElements.define('shipping-cutoff', ShippingCutoff);
})();
